import { useState, useEffect, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Search, X, ExternalLink, HelpCircle } from 'lucide-react'
import { contextGlossary, type GlossaryEntry } from '../../data/contextGlossary'
import './GlobalGlossary.css'

interface GlobalGlossaryProps {
  isOpen: boolean
  onClose: () => void
}

export function GlobalGlossary({ isOpen, onClose }: GlobalGlossaryProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedEntry, setSelectedEntry] = useState<string | null>(null)

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('')
      setSelectedEntry(null)
    }
  }, [isOpen])

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (selectedEntry) {
          setSelectedEntry(null)
        } else {
          onClose()
        }
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, selectedEntry, onClose])

  // Filter and sort entries
  const filteredEntries = useMemo(() => {
    const entries = Object.entries(contextGlossary)

    if (!searchQuery.trim()) {
      // Sort alphabetically when no search
      return entries.sort((a, b) => a[1].term.localeCompare(b[1].term))
    }

    const query = searchQuery.toLowerCase()
    return entries
      .filter(([key, entry]) =>
        entry.term.toLowerCase().includes(query) ||
        entry.definition.toLowerCase().includes(query) ||
        key.toLowerCase().includes(query)
      )
      .sort((a, b) => {
        // Prioritize term matches over definition matches
        const aTermMatch = a[1].term.toLowerCase().includes(query)
        const bTermMatch = b[1].term.toLowerCase().includes(query)
        if (aTermMatch && !bTermMatch) return -1
        if (!aTermMatch && bTermMatch) return 1
        return a[1].term.localeCompare(b[1].term)
      })
  }, [searchQuery])

  // Group entries by category
  const groupedEntries = useMemo(() => {
    const groups: Record<string, [string, GlossaryEntry][]> = {
      'Market Indicators': [],
      'Portfolio Metrics': [],
      'Technical Analysis': [],
      'Options': [],
      'Macroeconomic': [],
      'Other': [],
    }

    const categoryMap: Record<string, string> = {
      market_regime: 'Market Indicators',
      vix: 'Market Indicators',
      yield_curve: 'Macroeconomic',
      cipher_score: 'Portfolio Metrics',
      news_sentiment: 'Market Indicators',
      credit_spread: 'Macroeconomic',
      rsi: 'Technical Analysis',
      macd: 'Technical Analysis',
      bollinger_bands: 'Technical Analysis',
      insider_score: 'Portfolio Metrics',
      sharpe_ratio: 'Portfolio Metrics',
      var: 'Portfolio Metrics',
      cvar: 'Portfolio Metrics',
      max_drawdown: 'Portfolio Metrics',
      beta: 'Portfolio Metrics',
      iv_rank: 'Options',
      delta: 'Options',
      gamma: 'Options',
      theta: 'Options',
      vega: 'Options',
      rho: 'Options',
      put_call_ratio: 'Options',
      unusual_options: 'Options',
      gdp_growth: 'Macroeconomic',
      cpi: 'Macroeconomic',
      unemployment: 'Macroeconomic',
      fed_rate: 'Macroeconomic',
      treasury_10y: 'Macroeconomic',
      treasury_2y: 'Macroeconomic',
      geopolitical_risk: 'Macroeconomic',
      macro_regime: 'Macroeconomic',
      central_bank_stance: 'Macroeconomic',
      pe_ratio: 'Portfolio Metrics',
      forward_pe: 'Portfolio Metrics',
      squeeze_score: 'Market Indicators',
      short_interest: 'Market Indicators',
      days_to_cover: 'Market Indicators',
      analyst_rating: 'Market Indicators',
      price_target: 'Market Indicators',
      eps_revisions: 'Market Indicators',
      concentration_risk: 'Portfolio Metrics',
      momentum_score: 'Portfolio Metrics',
      fundamental_score: 'Portfolio Metrics',
      sentiment_score: 'Market Indicators',
      macro_score: 'Macroeconomic',
      stress_test: 'Portfolio Metrics',
      fifty_two_week_range: 'Technical Analysis',
      total_return: 'Portfolio Metrics',
      spy: 'Market Indicators',
      qqq: 'Market Indicators',
      day_change: 'Market Indicators',
      volume: 'Technical Analysis',
      market_cap: 'Portfolio Metrics',
      dividend_yield: 'Portfolio Metrics',
    }

    filteredEntries.forEach(entry => {
      const category = categoryMap[entry[0]] || 'Other'
      groups[category].push(entry)
    })

    // Remove empty groups
    return Object.fromEntries(
      Object.entries(groups).filter(([_, entries]) => entries.length > 0)
    )
  }, [filteredEntries])

  const selectedEntryData = selectedEntry ? contextGlossary[selectedEntry] : null

  if (!isOpen) return null

  const content = (
    <div className="gg-overlay" onClick={onClose}>
      <div className="gg-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="gg-header">
          <div className="gg-title">
            <HelpCircle size={20} />
            <h2>Financial Glossary</h2>
          </div>
          <button className="gg-close" onClick={onClose} aria-label="Close glossary">
            <X size={20} />
          </button>
        </div>

        {/* Search */}
        <div className="gg-search">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search metrics, indicators, terms..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
          {searchQuery && (
            <button className="gg-search-clear" onClick={() => setSearchQuery('')}>
              <X size={14} />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="gg-content">
          {selectedEntry && selectedEntryData ? (
            // Detail View
            <div className="gg-detail">
              <button className="gg-back" onClick={() => setSelectedEntry(null)}>
                &larr; Back to list
              </button>

              <h3 className="gg-detail-title">{selectedEntryData.term}</h3>

              <div className="gg-detail-section">
                <h4>Definition</h4>
                <p>{selectedEntryData.definition}</p>
              </div>

              {selectedEntryData.whyItMatters && (
                <div className="gg-detail-section">
                  <h4>Why It Matters</h4>
                  <p>{selectedEntryData.whyItMatters}</p>
                </div>
              )}

              {selectedEntryData.thresholds && selectedEntryData.thresholds.length > 0 && (
                <div className="gg-detail-section">
                  <h4>Interpretation Levels</h4>
                  <div className="gg-thresholds">
                    {selectedEntryData.thresholds.map((t, i) => (
                      <div key={i} className={`gg-threshold gg-${t.color}`}>
                        <span className="gg-threshold-label">{t.label}</span>
                        <span className="gg-threshold-value">{t.value}+</span>
                        <p className="gg-threshold-desc">{t.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedEntryData.signals && Object.keys(selectedEntryData.signals).length > 0 && (
                <div className="gg-detail-section">
                  <h4>Signal Interpretations</h4>
                  <div className="gg-signals">
                    {Object.entries(selectedEntryData.signals).map(([signal, data]) => (
                      <div key={signal} className={`gg-signal gg-${data.color}`}>
                        <span className="gg-signal-name">{signal}</span>
                        <p className="gg-signal-meaning">{data.meaning}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedEntryData.learnMoreUrl && (
                <a
                  href={selectedEntryData.learnMoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gg-learn-more"
                >
                  Learn More on Investopedia
                  <ExternalLink size={14} />
                </a>
              )}
            </div>
          ) : (
            // List View
            <div className="gg-list">
              {Object.entries(groupedEntries).map(([category, entries]) => (
                <div key={category} className="gg-category">
                  <h4 className="gg-category-title">{category}</h4>
                  <div className="gg-entries">
                    {entries.map(([key, entry]) => (
                      <button
                        key={key}
                        className="gg-entry"
                        onClick={() => setSelectedEntry(key)}
                      >
                        <span className="gg-entry-term">{entry.term}</span>
                        <span className="gg-entry-preview">
                          {entry.definition.slice(0, 80)}...
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {filteredEntries.length === 0 && (
                <div className="gg-no-results">
                  <p>No matching terms found</p>
                  <p className="gg-hint">Try a different search term</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="gg-footer">
          <span className="gg-shortcut">
            Press <kbd>Shift</kbd> + <kbd>?</kbd> to open this glossary
          </span>
          <span className="gg-count">{Object.keys(contextGlossary).length} terms</span>
        </div>
      </div>
    </div>
  )

  return createPortal(content, document.body)
}

// Hook for using the glossary with keyboard shortcut
export function useGlobalGlossary() {
  const [isOpen, setIsOpen] = useState(false)

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const toggle = useCallback(() => setIsOpen(prev => !prev), [])

  // Register keyboard shortcut (Shift + ?)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Shift + ? (which is Shift + /)
      if (e.shiftKey && e.key === '?') {
        e.preventDefault()
        toggle()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [toggle])

  return { isOpen, open, close, toggle }
}

// Help button component to open the glossary
export function GlossaryHelpButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      className="glossary-help-btn"
      onClick={onClick}
      title="Open Financial Glossary (Shift + ?)"
      aria-label="Open Financial Glossary"
    >
      <HelpCircle size={18} />
    </button>
  )
}

export default GlobalGlossary
